import React, { useState } from "react";
import { motion } from "framer-motion";
import {
  ArrowRight,
  CheckCircle2,
  X,
  FileText,
  ShieldCheck,
  Receipt,
  Megaphone,
  Users,
} from "lucide-react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { useLang } from "@/contexts/LanguageContext";
import SectionBadge from "@/components/SectionBadge";

const solutionIcons = {
  "documento-express": FileText,
  "documento-express-ar": ShieldCheck,
  "fatura-digital": Receipt,
  "documento-express-oficial": Megaphone,
  "documento-express-rh": Users,
};

const SolutionsSection = ({ onRequestDemo }) => {
  const { t } = useLang();
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(null);

  const openSheet = (sol) => {
    setActive(sol);
    setOpen(true);
  };

  return (
    <section
      id="solucoes"
      data-testid="solutions-section"
      className="relative py-24 lg:py-28"
    >
      <div
        aria-hidden
        className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[300px] glow-radial opacity-40 pointer-events-none"
      />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <div className="text-center max-w-3xl mx-auto mb-14">
          <SectionBadge>{t.solutions.eyebrow}</SectionBadge>
          <h2 className="mt-5 text-3xl sm:text-4xl lg:text-5xl font-bold leading-[1.08] tracking-tight text-white text-balance">
            {t.solutions.title}
          </h2>
          <p className="mt-4 text-[#b8c0cc] leading-relaxed">
            {t.solutions.subtitle}
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
          {t.solutions.items.map((s, i) => {
            const Icon = solutionIcons[s.id] || FileText;
            return (
              <motion.button
                key={s.id}
                type="button"
                onClick={() => openSheet(s)}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.15 }}
                transition={{ duration: 0.5, delay: i * 0.05 }}
                className="card-glow text-left group rounded-2xl bg-white/[0.02] border border-[#1dc7e9]/20 p-6 hover:border-[#1dc7e9]/55 hover:bg-white/[0.04] hover:-translate-y-1 hover:shadow-[0_10px_40px_rgba(29,199,233,0.18)] transition-all duration-300 flex flex-col"
                data-testid={`solution-card-${s.id}`}
              >
                <div className="mb-5">
                  <div className="w-14 h-14 rounded-xl border border-[#1dc7e9]/40 bg-[#1dc7e9]/5 flex items-center justify-center text-[#1dc7e9] group-hover:border-[#1dc7e9]/80 group-hover:shadow-[0_0_24px_rgba(29,199,233,0.35)] transition-all duration-300">
                    <Icon size={22} strokeWidth={1.6} />
                  </div>
                </div>

                <h3 className="text-[17px] font-bold text-white tracking-tight leading-snug">
                  {s.name}
                </h3>
                <p className="text-[#b8c0cc] text-[13px] mt-2 leading-relaxed flex-1">
                  {s.short}
                </p>

                <div className="mt-6 pt-4 border-t border-white/5 flex items-center justify-between">
                  <span className="text-[12px] font-semibold text-[#1dc7e9] group-hover:translate-x-0.5 transition-transform">
                    {t.solutions.cardLabel}
                  </span>
                  <ArrowRight
                    size={15}
                    className="text-[#1dc7e9] group-hover:translate-x-0.5 transition-transform"
                  />
                </div>
              </motion.button>
            );
          })}
        </div>

        <p className="text-center text-[12px] text-[#b8c0cc]/70 mt-8 mono">
          ▸ Clique em qualquer card para ver todos os detalhes da solução
        </p>
      </div>

      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent
          side="right"
          className="w-full sm:max-w-xl bg-[#0b1220] border-l border-[#1dc7e9]/20 text-white p-0 overflow-y-auto"
          data-testid="solution-drawer"
        >
          {active && (
            <div className="relative">
              <div
                aria-hidden
                className="absolute -top-32 -right-32 w-[400px] h-[400px] glow-radial opacity-60 pointer-events-none"
              />
              <SheetHeader className="p-6 pb-4 border-b border-[#1dc7e9]/10 relative">
                <button
                  onClick={() => setOpen(false)}
                  className="absolute right-4 top-4 w-8 h-8 rounded-full border border-[#1dc7e9]/20 flex items-center justify-center text-[#b8c0cc] hover:text-white hover:border-[#1dc7e9]/50 transition-all"
                  aria-label="Close"
                  data-testid="drawer-close"
                >
                  <X size={14} />
                </button>
                <span className="text-[10px] mono uppercase tracking-[0.25em] text-[#1dc7e9]">
                  {active.tag}
                </span>
                <SheetTitle className="text-2xl font-bold text-white tracking-tight text-left">
                  {active.name}
                </SheetTitle>
                <p className="text-[#b8c0cc] text-sm leading-relaxed text-left">
                  {active.short}
                </p>
              </SheetHeader>

              <div className="p-6 space-y-7 relative">
                <Block label={t.solutions.drawer.whatIs}>
                  <p className="text-[#b8c0cc] text-sm leading-relaxed">
                    {active.what}
                  </p>
                </Block>

                <Block label={t.solutions.drawer.features}>
                  <ul className="space-y-2">
                    {active.features.map((f, i) => (
                      <li
                        key={i}
                        className="flex items-start gap-2 text-sm text-white/90"
                      >
                        <CheckCircle2
                          size={15}
                          className="text-[#1dc7e9] mt-0.5 shrink-0"
                          strokeWidth={2}
                        />
                        {f}
                      </li>
                    ))}
                  </ul>
                </Block>

                <Block label={t.solutions.drawer.channels}>
                  <div className="flex flex-wrap gap-2">
                    {active.channels.map((c) => (
                      <span
                        key={c}
                        className="text-xs mono px-3 py-1.5 rounded-full border border-[#1dc7e9]/25 bg-[#1dc7e9]/5 text-[#1dc7e9]"
                      >
                        {c}
                      </span>
                    ))}
                  </div>
                </Block>

                <Block label={t.solutions.drawer.benefits}>
                  <ul className="space-y-2">
                    {active.benefits.map((b, i) => (
                      <li
                        key={i}
                        className="flex items-start gap-2 text-sm text-[#b8c0cc]"
                      >
                        <span className="w-1.5 h-1.5 rounded-full bg-[#1dc7e9] mt-2 shrink-0" />
                        {b}
                      </li>
                    ))}
                  </ul>
                </Block>

                <button
                  onClick={() => {
                    setOpen(false);
                    setTimeout(() => onRequestDemo?.(active.name), 200);
                  }}
                  className="btn-primary w-full inline-flex items-center justify-center gap-2 bg-[#1dc7e9] text-[#04060d] font-bold text-sm px-6 py-3.5 rounded-lg hover:shadow-[0_0_36px_rgba(29,199,233,0.5)] transition-all duration-300"
                  data-testid="drawer-cta"
                >
                  {t.solutions.drawer.cta}
                  <ArrowRight size={15} />
                </button>
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>
    </section>
  );
};

const Block = ({ label, children }) => (
  <div>
    <div className="text-[10px] mono uppercase tracking-[0.25em] text-[#1dc7e9] mb-3">
      {label}
    </div>
    {children}
  </div>
);

export default SolutionsSection;
